import reloadPage from '@Infra/Navigation/reloadPage';
import resolveServiceWorkerContainer from '@Infra/ServiceWorker/resolveServiceWorkerContainer';

export type RefreshApplicationOptions = {
	readonly container?: ServiceWorkerContainer;
	readonly reload?: () => void;
	readonly fallbackDelayMs?: number;
};

const DEFAULT_FALLBACK_DELAY_MS = 3000;

/**
 * Applies a waiting service worker update when there is one, otherwise reloads the page. The
 * `SKIP_WAITING` message goes to the registration's `waiting` worker, never the active controller.
 * Whichever comes first — `controllerchange` or the fallback delay — triggers a single reload.
 */
const refreshApplication = async (options: RefreshApplicationOptions = {}): Promise<void> => {
	const reload = options.reload ?? reloadPage;
	const fallbackDelayMs = options.fallbackDelayMs ?? DEFAULT_FALLBACK_DELAY_MS;
	const container = options.container ?? resolveServiceWorkerContainer();

	if (!container) {
		reload();
		return;
	}

	let waitingWorker: ServiceWorker | null | undefined;

	try {
		waitingWorker = (await container.getRegistration())?.waiting;
	} catch {
		reload();
		return;
	}

	if (!waitingWorker) {
		reload();
		return;
	}

	let hasReloaded = false;
	let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

	const reloadOnce = (): void => {
		if (hasReloaded) {
			return;
		}

		hasReloaded = true;
		container.removeEventListener('controllerchange', reloadOnce);
		clearTimeout(fallbackTimer);
		reload();
	};

	container.addEventListener('controllerchange', reloadOnce);
	fallbackTimer = setTimeout(reloadOnce, fallbackDelayMs);
	waitingWorker.postMessage({ type: 'SKIP_WAITING' });
};

export default refreshApplication;
